import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  User,
  Droplet,
  Gift,
  Calendar,
  Phone,
  MapPin,
  Shield,
  Award,
  Star,
  Users,
  TrendingUp,
  Edit,
  Check,
  Camera,
  Lock,
  Save,
  AlertCircle,
  X,
  Upload,
  Heart,
  Award as AwardIcon,
  Clock,
  CheckCircle,
  Mail,
  Home,
  UserCheck
} from 'lucide-react';
import api from '../../../config/api';
import { toast } from 'react-hot-toast';

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    profileImage: null,

    bloodGroup: '',
    age: '',
    weight: '',
    gender: '',

    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',

    hasDiseases: false,
    diseases: [],
    medications: [],

    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',

    isAvailable: true,

    totalDonations: 0,
    pendingRequests: 0,
    completedDonations: 0,
    lastDonation: null
  });

  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfileData();
  }, []);

  // Fetch profile data from the backend
  const fetchProfileData = async () => {
    setLoading(true);
    setError(null);

    try {
      // First try to get detailed profile
      let userData = null;
      let profileData = null;
      let statsData = {};

      try {
        const detailedResponse = await api.get('/auth/detailed-profile');
        userData = detailedResponse.data.user;
        profileData = detailedResponse.data.profile;
        statsData = detailedResponse.data.stats || {};
        console.log('Detailed profile data loaded successfully');
      } catch (detailedError) {
        console.log('Detailed profile endpoint not available, falling back to regular endpoints');

        // If detailed profile fails, fall back to regular profile endpoints
        const userResponse = await api.get('/auth/profile');
        userData = userResponse.data;

        // Check if user has donor role (either as single role or in roles array)
        const isDonor =
          userData.role === 'donor' ||
          (userData.roles && Array.isArray(userData.roles) && userData.roles.includes('donor'));

        // Try to get donor profile if user is a donor
        if (isDonor) {
          try {
            const donorResponse = await api.get('/api/donors/me');
            profileData = donorResponse.data;
          } catch (donorError) {
            console.error('Error fetching donor profile:', donorError);
          }
        }
      }

      // Map backend data to component state
      setProfileData({
        // User data
        fullName: userData?.name || '',
        email: userData?.email || '',
        phoneNumber: userData?.phone || '',
        profileImage: userData?.profileImage || null,

        // Donor data
        bloodGroup: profileData?.bloodType || '',
        age: profileData?.age || '',
        weight: profileData?.weight || '',
        gender: profileData?.gender || '',

        // Address
        addressLine1: profileData?.address?.street || '',
        addressLine2: '',
        city: profileData?.address?.city || '',
        state: profileData?.address?.state || '',
        postalCode: profileData?.address?.zipCode || '',
        country: profileData?.address?.country || '',

        // Medical history
        hasDiseases: profileData?.medicalHistory?.hasDiseases || false,
        diseases: profileData?.medicalHistory?.diseases || [],
        medications: profileData?.medicalHistory?.medications || [],

        // Emergency contact
        emergencyContactName: profileData?.emergencyContact?.name || '',
        emergencyContactRelationship: profileData?.emergencyContact?.relationship || '',
        emergencyContactPhone: profileData?.emergencyContact?.phone || '',

        // Availability
        isAvailable: profileData?.isAvailable !== undefined ? profileData.isAvailable : true,

        // Stats
        totalDonations: statsData?.totalDonations || profileData?.donationHistory?.length || 0,
        pendingRequests: statsData?.pendingRequests ||
          (profileData?.acceptedRequests?.filter(r => r.status === 'accepted' || r.status === 'pending')?.length || 0),
        completedDonations: statsData?.completedDonations ||
          (profileData?.acceptedRequests?.filter(r => r.status === 'donated')?.length || 0),
        lastDonation: statsData?.lastDonation ||
          (profileData?.donationHistory && profileData.donationHistory.length > 0 ?
            profileData.donationHistory[profileData.donationHistory.length - 1].date : null)
      });
    } catch (err) {
      console.error('Error fetching profile data:', err);
      setError('Failed to load profile data. Please try again.');
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPEG, PNG)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    // Create a preview
    const reader = new FileReader();
    reader.onload = () => {
      // Create an image element to resize the image
      const img = new Image();
      img.onload = () => {
        // Create a canvas to resize the image
        const canvas = document.createElement('canvas');

        // Calculate new dimensions (max 500px width/height)
        let width = img.width;
        let height = img.height;
        const maxSize = 500;

        if (width > height && width > maxSize) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else if (height > maxSize) {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }

        // Resize the image
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Get the resized image as a data URL with reduced quality
        const resizedImage = canvas.toDataURL('image/jpeg', 0.7);

        // Set the preview
        setImagePreview(resizedImage);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  // Save the uploaded image
  const saveProfileImage = async () => {
    if (!imagePreview) return;

    try {
      // Create a FormData object for the image upload
      const formData = new FormData();

      // Convert base64 to blob
      const fetchResponse = await fetch(imagePreview);
      const blob = await fetchResponse.blob();

      // Append the image file to the FormData
      formData.append('profileImage', blob, 'profile-image.jpg');
      formData.append('name', profileData.fullName);
      formData.append('email', profileData.email);
      formData.append('phone', profileData.phoneNumber);

      // Update profile with new image using FormData
      const response = await api.put('/auth/profile', {
        name: profileData.fullName,
        email: profileData.email,
        phone: profileData.phoneNumber,
        profileImage: imagePreview
      });

      // Update local state
      setProfileData(prev => ({
        ...prev,
        profileImage: imagePreview
      }));

      // Reset preview and close modal
      setImagePreview(null);
      setShowCameraModal(false);

      toast.success('Profile photo updated successfully!');
    } catch (err) {
      console.error('Error updating profile image:', err);
      toast.error('Failed to update profile photo. Please try a smaller image.');
    }
  };

  // Cancel image upload
  const cancelImageUpload = () => {
    setImagePreview(null);
    setShowCameraModal(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save profile data to the backend
  const saveProfile = async () => {
    setSaving(true);
    setError(null);

    try {
      // Prepare data for the backend
      const updateData = {
        // User data
        name: profileData.fullName,
        email: profileData.email,
        phone: profileData.phoneNumber,
        profileImage: profileData.profileImage,

        // Donor data
        bloodType: profileData.bloodGroup,
        age: parseInt(profileData.age),
        weight: parseInt(profileData.weight),
        gender: profileData.gender,

        // Address
        address: {
          street: profileData.addressLine1,
          city: profileData.city,
          state: profileData.state,
          zipCode: profileData.postalCode,
          country: profileData.country
        },

        // Medical history
        medicalHistory: {
          hasDiseases: profileData.hasDiseases,
          diseases: profileData.diseases,
          medications: profileData.medications
        },

        // Emergency contact
        emergencyContact: {
          name: profileData.emergencyContactName,
          relationship: profileData.emergencyContactRelationship,
          phone: profileData.emergencyContactPhone
        },

        // Availability
        isAvailable: profileData.isAvailable
      };

      // Send update request
      const response = await api.put('/auth/profile', updateData);

      toast.success('Profile updated successfully!');
      setIsEditing(false);

      // Refresh profile data
      fetchProfileData();
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEditing) {
      saveProfile();
    } else {
      setIsEditing(true);
    }
  };

  // Handle password change
  const handlePasswordChange = async (currentPassword, newPassword) => {
    try {
      await api.put('/auth/change-password', {
        currentPassword,
        newPassword
      });

      toast.success('Password changed successfully!');
      setShowPasswordModal(false);
    } catch (err) {
      console.error('Error changing password:', err);
      toast.error(err.response?.data?.message || 'Failed to change password');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-2xl shadow-xl p-5 relative overflow-hidden"
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-24 h-24 bg-white rounded-full -translate-y-12 translate-x-12"></div>
          <div className="absolute bottom-0 left-0 w-20 h-20 bg-white rounded-full translate-y-10 -translate-x-10"></div>
        </div>

        <div className="relative z-10">
          {/* Profile Info Section */}
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-4">
            {/* Profile Image */}
            <motion.div
              className="relative group cursor-pointer"
              whileHover={{ scale: 1.05 }}
              onClick={() => setShowCameraModal(true)}
            >
              <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center overflow-hidden border-4 border-white/20">
                {profileData.profileImage ? (
                  <img
                    src={profileData.profileImage}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-red-500" />
                )}
              </div>
              <div className="absolute inset-0 bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                <Camera className="w-5 h-5 text-white" />
              </div>
            </motion.div>

            {/* Profile Details */}
            <div className="text-center lg:text-left flex-1">
              <h1 className="text-2xl font-bold text-white mb-2">{profileData.fullName}</h1>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-4">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-xl text-xs text-white flex items-center gap-1.5 font-medium">
                  <Droplet className="w-3.5 h-3.5" />
                  {profileData.bloodGroup || 'Not specified'}
                </span>
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-xl text-xs text-white flex items-center gap-1.5 font-medium">
                  <Heart className="w-3.5 h-3.5" />
                  {profileData.totalDonations} donations
                </span>
                <span className={`px-4 py-2 rounded-2xl text-sm font-medium flex items-center gap-2 ${profileData.isAvailable
                    ? 'bg-green-500/20 text-green-100'
                    : 'bg-yellow-500/20 text-yellow-100'
                  }`}>
                  <UserCheck className="w-4 h-4" />
                  {profileData.isAvailable ? 'Available' : 'Unavailable'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <motion.button
              onClick={handleSubmit}
              disabled={saving}
              className="flex-1 sm:flex-none px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold backdrop-blur-sm text-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {saving ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent"></div>
                  Saving...
                </>
              ) : isEditing ? (
                <>
                  <Save className="w-5 h-5" />
                  Save Changes
                </>
              ) : (
                <>
                  <Edit className="w-5 h-5" />
                  Edit Profile
                </>
              )}
            </motion.button>
            <motion.button
              onClick={() => setShowPasswordModal(true)}
              className="flex-1 sm:flex-none px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center gap-2 transition-all duration-200 font-semibold backdrop-blur-sm text-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Lock className="w-4 h-4" />
              Change Password
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Left Column - Personal & Contact Info */}
        <div className="md:col-span-2 space-y-4">
          {/* Personal Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-200 p-5"
          >
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={profileData.fullName}
                  onChange={(e) => handleProfileChange('fullName', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                <select
                  value={profileData.bloodGroup}
                  onChange={(e) => handleProfileChange('bloodGroup', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                <input
                  type="number"
                  value={profileData.age}
                  onChange={(e) => handleProfileChange('age', e.target.value)}
                  disabled={!isEditing}
                  min="18"
                  max="65"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
                <input
                  type="number"
                  value={profileData.weight}
                  onChange={(e) => handleProfileChange('weight', e.target.value)}
                  disabled={!isEditing}
                  min="45"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={profileData.gender}
                  onChange={(e) => handleProfileChange('gender', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Availability Status</label>
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="isAvailable"
                    checked={profileData.isAvailable}
                    onChange={(e) => handleProfileChange('isAvailable', e.target.checked)}
                    disabled={!isEditing}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isAvailable" className="ml-2 block text-sm text-gray-700">
                    Available for donation
                  </label>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-200 p-5"
          >
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={profileData.phoneNumber}
                  onChange={(e) => handleProfileChange('phoneNumber', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>
          </motion.div>

          {/* Address Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-200 p-5"
          >
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Home className="w-4 h-4 text-white" />
              </div>
              Address Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={profileData.addressLine1}
                  onChange={(e) => handleProfileChange('addressLine1', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={profileData.city}
                  onChange={(e) => handleProfileChange('city', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={profileData.state}
                  onChange={(e) => handleProfileChange('state', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                <input
                  type="text"
                  value={profileData.postalCode}
                  onChange={(e) => handleProfileChange('postalCode', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  value={profileData.country}
                  onChange={(e) => handleProfileChange('country', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>
          </motion.div>

          {/* Emergency Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-200 p-5"
          >
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800">
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-white" />
              </div>
              Emergency Contact
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={profileData.emergencyContactName}
                  onChange={(e) => handleProfileChange('emergencyContactName', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <input
                  type="text"
                  value={profileData.emergencyContactRelationship}
                  onChange={(e) => handleProfileChange('emergencyContactRelationship', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  value={profileData.emergencyContactPhone}
                  onChange={(e) => handleProfileChange('emergencyContactPhone', e.target.value)}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column - Stats & Achievements */}
        <div className="space-y-4">
          {/* Donation Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-200 p-4"
          >
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-gray-800">
              <div className="w-8 h-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                <Activity className="w-4 h-4 text-white" />
              </div>
              Donation Statistics
            </h3>
            <div className="space-y-3">
              <motion.div
                className="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-red-100 rounded-2xl border border-red-200"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">Total Donations</p>
                    <p className="text-2xl font-bold text-gray-900">{profileData.totalDonations}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl border border-blue-200"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">Pending Requests</p>
                    <p className="text-2xl font-bold text-gray-900">{profileData.pendingRequests}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-green-100 rounded-2xl border border-green-200"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">Completed</p>
                    <p className="text-2xl font-bold text-gray-900">{profileData.completedDonations}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl border border-purple-200"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">Last Donation</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {profileData.lastDonation ? new Date(profileData.lastDonation).toLocaleDateString() : 'No donations yet'}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Medical Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-gray-500" />
              Medical Information
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    id="hasDiseases"
                    checked={profileData.hasDiseases}
                    onChange={(e) => handleProfileChange('hasDiseases', e.target.checked)}
                    disabled={!isEditing}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasDiseases" className="ml-2 block text-sm text-gray-700">
                    Has medical conditions
                  </label>
                </div>
                {profileData.hasDiseases && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medical Conditions</label>
                    <textarea
                      value={profileData.diseases.join(', ')}
                      onChange={(e) => handleProfileChange('diseases', e.target.value.split(',').map(item => item.trim()))}
                      disabled={!isEditing}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                      placeholder="Enter medical conditions separated by commas"
                      rows="2"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Medications</label>
                <textarea
                  value={profileData.medications.join(', ')}
                  onChange={(e) => handleProfileChange('medications', e.target.value.split(',').map(item => item.trim()))}
                  disabled={!isEditing}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder="Enter medications separated by commas"
                  rows="2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Change Password</h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <PasswordChangeForm onSubmit={handlePasswordChange} onCancel={() => setShowPasswordModal(false)} />
          </div>
        </div>
      )}

      {/* Photo Upload Modal */}
      {showCameraModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Update Profile Photo</h3>
              <button
                onClick={cancelImageUpload}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {imagePreview ? (
                <div className="flex flex-col items-center">
                  <div className="w-32 h-32 rounded-full overflow-hidden border-2 border-red-500">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Preview</p>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <Upload className="w-10 h-10 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500 mb-2">Click to select or drag an image</p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/jpeg, image/png, image/jpg"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={cancelImageUpload}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={saveProfileImage}
                  disabled={!imagePreview}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Save Photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Password Change Form Component
const PasswordChangeForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate passwords
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData.currentPassword, formData.newPassword);
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="text-sm">{error}</p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
        <input
          type="password"
          name="currentPassword"
          value={formData.currentPassword}
          onChange={handleChange}
          required
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
        <input
          type="password"
          name="newPassword"
          value={formData.newPassword}
          onChange={handleChange}
          required
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
        <input
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {loading ? 'Changing...' : 'Change Password'}
        </button>
      </div>
    </form>
  );
};

export default Profile;